import { create } from "zustand";
import { createRequestDeduplicator } from "../utils/inFlightRequests";
import { fetchWithApiError } from "../utils/analyticsFetch";
import { authenticatedFetch } from "../utils/authFetch";
import type { LessonRevisionFields } from "../utils/contentRevisions";

export interface Course {
  id: number;
  title: string;
  description?: string;
  image_url?: string;
  instructor_name: string;
  level: string;
  language: string;
  programming_language?: string;
  num_students: number;
}

export interface Unit {
  id: number;
  course_id: number;
  title: string;
  description?: string;
  position: number;
  image_url?: string;
}

export interface LessonOverview extends LessonRevisionFields {
  id: number;
  unit_id: number;
  title: string;
  description?: string;
  position: number;
  objectives: string;
  video_url: string;
  has_quiz: boolean;
  has_coding: boolean;
}

export interface QuizOption {
  id: number;
  option_text: string;
  is_correct: boolean;
}

export interface QuizShortAnswer {
  id: number;
  model_answer: string;
  must_match_model_answer: boolean;
  keywords?: string;
  min_words?: number;
  max_words?: number;
}

export interface QuizQuestion {
  id: number;
  question_text: string;
  question_type: "multiple_choice" | "short_answer";
  objectives: string;
  position?: number;
  is_generated?: boolean;
  replaces_question_id?: number | null;
  created_at?: string;
  options?: QuizOption[];
  short_answer?: QuizShortAnswer;
  student_answer?: QuizAnswerSnapshot;
}

export interface QuizAnswerSnapshot {
  selected_option_id?: number;
  short_answer?: string;
  is_correct?: boolean;
  answered_at?: string;
}

export interface QuizAttemptChain {
  question_index: number;
  root_question_id: number;
  latest_question_id: number;
  attempts: QuizQuestion[];
}

export interface Quiz {
  id: number;
  title: string;
  questions: QuizQuestion[];
  attempt_chains?: QuizAttemptChain[];
}

export interface CodingExerciseType {
  id: number;
  description: string;
  starter_code: string;
  programming_language: string;
  objectives: string;
  model_answer: string;
  test_cases?: {
    input: string;
    expected_output: string;
  }[];
}

export interface LessonDetail extends LessonOverview {
  quiz?: Quiz;
  coding_exercises?: CodingExerciseType[];
}

// Cache interfaces
export interface CachedData<T> {
  data: T;
  fetchedAt: number;
}

// Define a TTL (time-to-live) duration in milliseconds
const TTL = 5 * 60 * 1000; // 5 minutes instead of 500ms

const courseStorageKey = (userId: string) => `currentCourseId:${userId}`;
const unitStorageKey = (userId: string) => `currentUnitId:${userId}`;
const lessonStorageKey = (userId: string, unitId: number) =>
  `currentLessonId:${userId}:${unitId}`;

export const migrateLegacyCourseUnit = (userId: string) => {
  try {
    const legacyCourseId = localStorage.getItem("currentCourseId");
    const legacyUnitId = localStorage.getItem("currentUnitId");
    const hasUserCourse = localStorage.getItem(courseStorageKey(userId));
    const hasUserUnit = localStorage.getItem(unitStorageKey(userId));

    if (!hasUserCourse && legacyCourseId) {
      localStorage.setItem(courseStorageKey(userId), legacyCourseId);
    }
    if (!hasUserUnit && legacyUnitId) {
      localStorage.setItem(unitStorageKey(userId), legacyUnitId);
    }

    if (legacyCourseId) {
      localStorage.removeItem("currentCourseId");
    }
    if (legacyUnitId) {
      localStorage.removeItem("currentUnitId");
    }
  } catch (error) {
    console.error("Error migrating legacy course/unit storage:", error);
  }
};

export const getPersistedCourseId = (userId: string): number | null => {
  try {
    const value = localStorage.getItem(courseStorageKey(userId));
    return value ? parseInt(value) : null;
  } catch (error) {
    console.error("Error loading persisted course id:", error);
    return null;
  }
};

export const getPersistedUnitId = (userId: string): number | null => {
  try {
    const value = localStorage.getItem(unitStorageKey(userId));
    return value ? parseInt(value) : null;
  } catch (error) {
    console.error("Error loading persisted unit id:", error);
    return null;
  }
};

export const getPersistedLessonId = (
  userId: string,
  unitId: number
): number | null => {
  try {
    const value = localStorage.getItem(lessonStorageKey(userId, unitId));
    return value ? parseInt(value) : null;
  } catch (error) {
    console.error("Error loading persisted lesson id:", error);
    return null;
  }
};

// Load initial state from localStorage
const loadPersistedState = () => {
  try {
    return {
      currentCourseId: null,
      currentUnitId: null,
      currentLessonId: null,
    };
  } catch (error) {
    console.error("Error loading persisted state:", error);
    return {
      currentCourseId: null,
      currentUnitId: null,
      currentLessonId: null,
    };
  }
};

const initialState = loadPersistedState();

interface CoursesState {
  currentCourseId: number | null;
  setCurrentCourseId: (
    courseId: number,
    options?: { userId?: string }
  ) => void;
  currentUnitId: number | null;
  setCurrentUnitId: (unitId: number, options?: { userId?: string }) => void;
  currentLessonId: number | null;
  setCurrentLessonId: (
    lessonId: number | null,
    options?: { userId?: string; unitId?: number; clearPersisted?: boolean }
  ) => void;
  clearCourseUnitLesson: () => void;
  invalidateUnitContent: (unitId: number, courseId?: number) => void;

  courses: Record<number, CachedData<Course> | null>;
  units: Record<number, CachedData<Unit[]> | null>;
  lessonsOverview: Record<number, CachedData<LessonOverview[]> | null>;
  lessonsContent: Record<number, CachedData<LessonDetail> | null>;

  fetchCourses: (force?: boolean) => Promise<void>;
  fetchUnits: (courseId: number, force?: boolean) => Promise<void>;
  fetchLessonsOverview: (unitId: number, force?: boolean) => Promise<void>;
  fetchLessonContent: (
    lessonId: number,
    studentId: string,
    force?: boolean
  ) => Promise<void>;
  patchLessonQuizAttemptChain: (
    lessonId: number,
    questionIndex: number,
    attempt: QuizQuestion
  ) => void;
}

const deduplicateContentRequest = createRequestDeduplicator();

let coursesRequestInFlight: Promise<void> | null = null;

const useCoursesStore = create<CoursesState>()((set, get) => ({
  currentCourseId: initialState.currentCourseId,
  setCurrentCourseId: (courseId, options) => {
    if (options?.userId) {
      localStorage.setItem(
        courseStorageKey(options.userId),
        courseId.toString()
      );
    }
    set({ currentCourseId: courseId });
  },
  currentUnitId: initialState.currentUnitId,
  setCurrentUnitId: (unitId, options) => {
    if (options?.userId) {
      localStorage.setItem(unitStorageKey(options.userId), unitId.toString());
    }
    set({ currentUnitId: unitId });
  },
  currentLessonId: initialState.currentLessonId,
  setCurrentLessonId: (lessonId, options) => {
    const userId = options?.userId;
    const unitId = options?.unitId;
    if (lessonId !== null && userId && unitId) {
      localStorage.setItem(
        lessonStorageKey(userId, unitId),
        lessonId.toString()
      );
    } else if (lessonId === null && options?.clearPersisted && userId && unitId) {
      localStorage.removeItem(lessonStorageKey(userId, unitId));
    }
    set({ currentLessonId: lessonId });
  },
  clearCourseUnitLesson: () => {
    set({
      currentCourseId: null,
      currentUnitId: null,
      currentLessonId: null,
    });
  },
  invalidateUnitContent: (unitId, courseId) => {
    set((state) => {
      const lessonIds = state.lessonsOverview[unitId]?.data.map((lesson) => lesson.id) ?? [];
      const lessonsContent = { ...state.lessonsContent };
      lessonIds.forEach((lessonId) => {
        delete lessonsContent[lessonId];
      });
      return {
        ...(courseId === undefined ? {} : { units: { ...state.units, [courseId]: null } }),
        lessonsOverview: { ...state.lessonsOverview, [unitId]: null },
        lessonsContent,
      };
    });
  },

  courses: {},
  units: {},
  lessonsOverview: {},
  lessonsContent: {},

  fetchCourses: async (force = false) => {
    // Check if we have valid cached data
    const hasValidCache = Object.values(get().courses).some(
      (course) => course && Date.now() - course.fetchedAt < TTL
    );

    if (hasValidCache && !force) return;
    if (coursesRequestInFlight) return coursesRequestInFlight;

    const request = (async () => {
      const response = await fetchWithApiError(
        `${import.meta.env.VITE_BACKEND_API_URL}/courses/`
      );
      if (!response.ok) throw new Error("Failed to fetch courses");
      const data: Course[] = await response.json();

      // Store each course individually with timestamp
      const coursesWithTimestamp = data.reduce(
        (acc, course) => ({
          ...acc,
          [course.id]: { data: course, fetchedAt: Date.now() },
        }),
        {}
      );

      set({ courses: coursesWithTimestamp });
    })();

    coursesRequestInFlight = request;

    try {
      await request;
    } catch (error) {
      console.error("Error fetching courses:", error);
      throw error;
    } finally {
      if (coursesRequestInFlight === request) {
        coursesRequestInFlight = null;
      }
    }
  },

  fetchUnits: (courseId: number, force = false) => deduplicateContentRequest(`units:${courseId}:${force}`, async () => {
    const cached = get().units[courseId];
    if (!force && cached && Date.now() - cached.fetchedAt < TTL) return;

    try {
      const response = await fetchWithApiError(
        `${import.meta.env.VITE_BACKEND_API_URL}/courses/${courseId}/units/`
      );
      if (!response.ok) throw new Error("Failed to fetch units");
      const data: Unit[] = await response.json();

      set({
        units: {
          ...get().units,
          [courseId]: { data, fetchedAt: Date.now() },
        },
      });
    } catch (error) {
      console.error("Error fetching units:", error);
      throw error;
    }
  }),

  fetchLessonsOverview: (unitId: number, force = false) => deduplicateContentRequest(`overview:${unitId}:${force}`, async () => {
    const cached = get().lessonsOverview[unitId];
    if (!force && cached && Date.now() - cached.fetchedAt < TTL) return;

    try {
      const response = await fetchWithApiError(
        `${import.meta.env.VITE_BACKEND_API_URL}/units/${unitId}/lessons/`
      );
      if (!response.ok) throw new Error("Failed to fetch lessons overview");
      const data: LessonOverview[] = await response.json();

      set({
        lessonsOverview: {
          ...get().lessonsOverview,
          [unitId]: { data, fetchedAt: Date.now() },
        },
      });
    } catch (error) {
      console.error("Error fetching lessons overview:", error);
      throw error;
    }
  }),

  fetchLessonContent: (
    lessonId: number,
    studentId: string,
    force = false
  ) => deduplicateContentRequest(`lesson:${studentId}:${lessonId}:${force}`, async () => {
    const cached = get().lessonsContent[lessonId];
    if (!force && cached && Date.now() - cached.fetchedAt < TTL) return;

    try {
      const response = await authenticatedFetch(
        `${import.meta.env.VITE_BACKEND_API_URL}/lessons/${lessonId}/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ student_id: studentId }),
        },
        { studentId }
      );
      if (!response.ok) throw new Error("Failed to fetch lesson content");
      const data: LessonDetail = await response.json();

      set({
        lessonsContent: {
          ...get().lessonsContent,
          [lessonId]: { data, fetchedAt: Date.now() },
        },
      });
    } catch (error) {
      console.error("Error fetching lesson content:", error);
      throw error;
    }
  }),
  patchLessonQuizAttemptChain: (lessonId, questionIndex, attempt) => {
    set((state) => {
      const lessonCache = state.lessonsContent[lessonId];
      const lessonData = lessonCache?.data;
      const quiz = lessonData?.quiz;
      if (!lessonCache || !lessonData || !quiz) {
        return state;
      }

      const fallbackChains: QuizAttemptChain[] = quiz.questions.map((question, index) => ({
        question_index: index,
        root_question_id: question.id,
        latest_question_id: question.id,
        attempts: [question],
      }));
      const existingChains =
        quiz.attempt_chains && quiz.attempt_chains.length > 0
          ? quiz.attempt_chains
          : fallbackChains;

      if (questionIndex < 0 || questionIndex >= existingChains.length) {
        return state;
      }

      const targetChain = existingChains[questionIndex];
      const attemptExists = targetChain.attempts.some(
        (currentAttempt) => currentAttempt.id === attempt.id
      );

      const nextAttempts = attemptExists
        ? targetChain.attempts.map((currentAttempt) =>
            currentAttempt.id === attempt.id ? attempt : currentAttempt
          )
        : [...targetChain.attempts, attempt];

      nextAttempts.sort((left, right) => {
        const leftCreatedAt = left.created_at ?? "";
        const rightCreatedAt = right.created_at ?? "";
        if (leftCreatedAt === rightCreatedAt) {
          return left.id - right.id;
        }
        return leftCreatedAt.localeCompare(rightCreatedAt);
      });

      const latestAttempt = nextAttempts[nextAttempts.length - 1];
      const updatedChains = existingChains.map((chain, index) => {
        if (index !== questionIndex) {
          return chain;
        }
        return {
          ...chain,
          latest_question_id: latestAttempt.id,
          attempts: nextAttempts,
        };
      });

      const updatedQuestions = [...quiz.questions];
      if (updatedQuestions[questionIndex]) {
        updatedQuestions[questionIndex] = latestAttempt;
      }

      return {
        lessonsContent: {
          ...state.lessonsContent,
          [lessonId]: {
            ...lessonCache,
            fetchedAt: Date.now(),
            data: {
              ...lessonData,
              quiz: {
                ...quiz,
                questions: updatedQuestions,
                attempt_chains: updatedChains,
              },
            },
          },
        },
      };
    });
  },
}));

export default useCoursesStore;
